import Phaser from 'phaser';
import { THEME } from '@/config/theme';

export interface HitZoneConfig {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class HitZone extends Phaser.GameObjects.Container {
  private background: Phaser.GameObjects.Rectangle;
  private border: Phaser.GameObjects.Rectangle;
  private flashOverlay: Phaser.GameObjects.Rectangle;
  private originalX: number;

  constructor(scene: Phaser.Scene, config: HitZoneConfig) {
    super(scene, config.x, config.y);
    this.originalX = config.x;

    // Background glow
    this.background = scene.add.rectangle(0, 0, config.width, config.height, THEME.colors.primaryHex, 0.2);
    this.add(this.background);

    // Border
    this.border = scene.add.rectangle(0, 0, config.width, config.height);
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
    this.border.setFillStyle(0x000000, 0);
    this.add(this.border);

    // Flash overlay for hit feedback
    this.flashOverlay = scene.add.rectangle(0, 0, config.width, config.height, 0xffffff, 0);
    this.add(this.flashOverlay);

    scene.add.existing(this);

    // Subtle pulsing animation
    scene.tweens.add({
      targets: this.border,
      alpha: 0.6,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /**
   * Flash the hit zone for feedback
   */
  flash(color: number = THEME.colors.primaryHex, duration: number = 100): void {
    this.flashOverlay.setFillStyle(color, 0.5);

    this.scene.tweens.add({
      targets: this.flashOverlay,
      alpha: { from: 0.5, to: 0 },
      duration,
      onComplete: () => {
        this.flashOverlay.setAlpha(0);
      },
    });
  }

  /**
   * Flash green for successful hit
   */
  flashSuccess(): void {
    this.flash(THEME.colors.hit.perfectHex, 100);
  }

  /**
   * Flash yellow for good hit
   */
  flashGood(): void {
    this.flash(THEME.colors.hit.goodHex, 100);
  }

  /**
   * Flash orange for OK hit
   */
  flashOk(): void {
    this.flash(THEME.colors.hit.okHex, 100);
  }

  /**
   * Flash red for miss
   */
  flashMiss(): void {
    this.flash(THEME.colors.hit.missHex, 150);

    // Shake effect - always return to original position
    this.scene.tweens.add({
      targets: this,
      x: this.originalX + 5,
      duration: 50,
      yoyo: true,
      repeat: 3,
      onComplete: () => {
        this.x = this.originalX;
      },
    });
  }

  /**
   * Update the hit zone appearance based on streak
   */
  updateStreak(streak: number): void {
    let color: number = THEME.colors.primaryHex;
    let glowIntensity = 0.2;

    if (streak >= 50) {
      color = THEME.colors.magentaHex;
      glowIntensity = 0.5;
    } else if (streak >= 25) {
      color = THEME.colors.cyanHex;
      glowIntensity = 0.4;
    } else if (streak >= 10) {
      color = THEME.colors.yellowHex;
      glowIntensity = 0.3;
    }

    this.border.setStrokeStyle(3, color);
    this.background.setFillStyle(color, glowIntensity);
  }

  /**
   * Reset to default appearance
   */
  reset(): void {
    this.border.setStrokeStyle(3, THEME.colors.primaryHex);
    this.background.setFillStyle(THEME.colors.primaryHex, 0.2);
  }
}
